/**
 * The header breadcrumb's route map.
 *
 * A pure function, in its own module, so the mapping can be tested without
 * mounting the router — and so adding a route means adding a rule here rather
 * than tracing one through JSX.
 */

import { TICKETS_PATH } from "@/features/admin/ticketsPath";

export interface Crumb {
  label: string;
  to: string;
}

/** Solutions placeholder routes → [group, leaf]. */
const SOLUTION_CRUMBS: Record<string, [string, string] | undefined> = {
  "/solutions/create": ["Base", "Create"],
  "/solutions/review": ["Base", "Review"],
  "/solutions/quality": ["Base", "Quality"],
  "/solutions/clinical/etmf-intake": ["Clinical", "eTMF intake"],
  "/solutions/clinical/etmf-completeness": ["Clinical", "eTMF completeness"],
  "/solutions/clinical/assignments": ["Clinical", "Assignments"],
};

/** `/solutions/create/<type>` → leaf label. */
const CREATE_TYPE_CRUMBS: Record<string, string | undefined> = {
  translate: "Translate",
  qa: "Q&A",
};

export function crumbsFor(pathname: string): Crumb[] {
  const items: Crumb[] = [{ label: "DAQUMA", to: "/" }];

  if (pathname === "/") {
    items.push({ label: "Home", to: "/" });
  } else if (pathname === "/me") {
    items.push({ label: "My profile", to: "/me" });
  } else if (pathname.startsWith("/settings")) {
    items.push({ label: "Settings", to: "/settings" });
  } else if (pathname.startsWith("/notifications")) {
    items.push({ label: "Notifications", to: "/notifications" });
  } else if (pathname.startsWith("/admin/users/new")) {
    items.push(
      { label: "User management", to: "/admin/users" },
      { label: "New user", to: "/admin/users/new" },
    );
  } else if (pathname.startsWith("/admin/users/")) {
    items.push({ label: "User management", to: "/admin/users" }, { label: "User", to: pathname });
  } else if (pathname.startsWith("/admin/users")) {
    items.push({ label: "User management", to: "/admin/users" });
  } else if (pathname.startsWith("/admin/approvals/veeva/")) {
    items.push(
      { label: "Validation", to: TICKETS_PATH },
      { label: "Support tickets", to: TICKETS_PATH },
      { label: "Veeva approval", to: pathname },
    );
  } else if (pathname.startsWith(TICKETS_PATH)) {
    // Support tickets (ADR-0069): Validation › Support tickets (› Ticket).
    items.push(
      { label: "Validation", to: TICKETS_PATH },
      { label: "Support tickets", to: TICKETS_PATH },
    );
    if (pathname.startsWith(`${TICKETS_PATH}/`)) items.push({ label: "Ticket", to: pathname });
  } else if (pathname.startsWith("/admin/audit")) {
    items.push({ label: "Audit log", to: "/admin/audit" });
  } else if (pathname.startsWith("/integration/connections/new")) {
    items.push(
      { label: "Integrations", to: "/integration/connections" },
      { label: "New connection", to: "/integration/connections/new" },
    );
  } else if (pathname.startsWith("/integration/connections/")) {
    items.push(
      { label: "Integrations", to: "/integration/connections" },
      { label: "Connection", to: pathname },
    );
  } else if (pathname.startsWith("/integration/connections")) {
    items.push(
      { label: "Integrations", to: "/integration/connections" },
      {
        label: "Connected",
        to: "/integration/connections",
      },
    );
  } else if (pathname.startsWith("/integration/available")) {
    items.push(
      { label: "Integrations", to: "/integration/connections" },
      { label: "Add connection", to: "/integration/available" },
    );
  } else if (pathname.startsWith("/integration")) {
    items.push({ label: "Integrations", to: "/integration/connections" });
  } else if (pathname.startsWith("/browse/veeva")) {
    items.push({ label: "Data browser", to: pathname }, { label: "Veeva Vault", to: pathname });
  } else if (pathname.startsWith("/browse/cara")) {
    items.push({ label: "Data browser", to: pathname }, { label: "Generis CARA", to: pathname });
  } else if (pathname.startsWith("/browse/blob")) {
    items.push(
      { label: "Data browser", to: pathname },
      { label: "Azure Blob Storage", to: pathname },
    );
  } else if (pathname.startsWith("/browse/database")) {
    items.push({ label: "Data browser", to: pathname }, { label: "Database", to: pathname });
  } else if (pathname.startsWith("/browse/s3")) {
    items.push({ label: "Data browser", to: pathname }, { label: "AWS S3", to: pathname });
  } else if (pathname.startsWith("/browse/sharepoint")) {
    items.push({ label: "Data browser", to: pathname }, { label: "SharePoint", to: pathname });
  } else if (pathname === "/projects/new") {
    items.push({ label: "Projects", to: "/projects" }, { label: "New project", to: pathname });
  } else if (pathname === "/projects/approvals") {
    // A tab of the Projects hub, not a project — so this has to come before the
    // `/projects/` branch, which would otherwise read "approvals" as an id.
    items.push({ label: "Projects", to: "/projects" }, { label: "Approvals", to: pathname });
  } else if (pathname.startsWith("/projects/")) {
    // A project's own page, and the pipelines beneath it. Neither name is in
    // the URL, so the leaves are the generic words — each page puts the real
    // name in its own title.
    const projectPath = pathname.split("/").slice(0, 3).join("/");
    items.push({ label: "Projects", to: "/projects" }, { label: "Project", to: projectPath });
    if (pathname.endsWith("/pipelines/new")) {
      items.push({ label: "New pipeline", to: pathname });
    } else if (pathname.includes("/pipelines/")) {
      items.push({ label: "Pipeline", to: pathname });
    }
  } else if (pathname.startsWith("/projects")) {
    items.push({ label: "Projects", to: "/projects" });
  } else if (pathname.startsWith("/solutions/explore/")) {
    // A chat's name is not in the URL; the page carries it in its own title.
    items.push(
      { label: "Base", to: "/solutions/explore" },
      { label: "Explore", to: "/solutions/explore" },
      { label: "Chat", to: pathname },
    );
  } else if (pathname.startsWith("/solutions/validation/second-line-support")) {
    const base = "/solutions/validation/second-line-support";
    items.push({ label: "Validation", to: base }, { label: "2nd line support", to: base });
    if (pathname.startsWith(`${base}/`)) items.push({ label: "Chat", to: pathname });
  } else if (pathname.startsWith("/solutions/validation/first-line-support")) {
    const base = "/solutions/validation/first-line-support";
    items.push({ label: "Validation", to: base }, { label: "1st line support", to: base });
    if (pathname.startsWith(`${base}/`)) items.push({ label: "Chat", to: pathname });
  } else if (pathname === "/solutions/explore") {
    items.push(
      { label: "Base", to: "/solutions/explore" },
      { label: "Explore", to: "/solutions/explore" },
    );
  } else if (pathname.startsWith("/validation/risk-assessment")) {
    // A tab of the runs page (ADR-0064).
    items.push(
      { label: "Release validation runs", to: "/validation/runs" },
      { label: "Risk assessments", to: "/validation/risk-assessment" },
    );
  } else if (pathname.startsWith("/validation/settings")) {
    items.push(
      { label: "Release validation runs", to: "/validation/runs" },
      { label: "Settings", to: "/validation/settings" },
    );
  } else if (pathname === "/validation/runs/new") {
    items.push(
      { label: "Release validation runs", to: "/validation/runs" },
      { label: "New run", to: pathname },
    );
  } else if (pathname.startsWith("/validation/runs/")) {
    items.push(
      { label: "Release validation runs", to: "/validation/runs" },
      { label: "Run", to: pathname },
    );
  } else if (pathname.startsWith("/validation/runs")) {
    items.push({ label: "Release validation runs", to: "/validation/runs" });
  } else if (pathname.startsWith("/validation/my-tests")) {
    items.push({ label: "My tests", to: "/validation/my-tests" });
  } else if (pathname === "/development/collections/new") {
    items.push(
      { label: "Collections", to: "/development/collections" },
      { label: "New collection", to: pathname },
    );
  } else if (pathname.startsWith("/development/collections/")) {
    // The name is not in the URL; the page carries it in its own title.
    items.push(
      { label: "Collections", to: "/development/collections" },
      { label: "Collection", to: pathname },
    );
  } else if (pathname.startsWith("/development/collections")) {
    items.push({ label: "Collections", to: "/development/collections" });
  } else if (pathname.startsWith("/admin/documentation")) {
    items.push({ label: "Documentation", to: "/admin/documentation" });
  } else if (pathname.startsWith("/solutions/create/")) {
    // Create's type pages sit under the type picker.
    const leaf = CREATE_TYPE_CRUMBS[pathname.split("/")[3] ?? ""] ?? "Create";
    items.push(
      { label: "Base", to: "/solutions/create" },
      { label: "Create", to: "/solutions/create" },
      { label: leaf, to: pathname },
    );
  } else if (SOLUTION_CRUMBS[pathname]) {
    // Group headings (Base / Validation / Clinical) are not routes; like the
    // Data browser crumb they point at the page itself.
    const [group, leaf] = SOLUTION_CRUMBS[pathname];
    items.push({ label: group, to: pathname }, { label: leaf, to: pathname });
  }
  // No `/modules/` branch: the last placeholder page (Data Analytics) is gone,
  // and the two remaining `/modules/*` paths are redirects, so nothing under
  // that prefix ever renders a page to put a crumb on.

  return items;
}
